import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import ModuleGate from "@/components/base/ModuleGate";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import type { Invitation } from "@/pages/admin/invitations/components/InvitationModal";

import GeneralTab     from "./tabs/GeneralTab";
import EventDetailsTab from "./tabs/EventDetailsTab";
import DesignTab      from "./tabs/DesignTab";
import MediaTab       from "./tabs/MediaTab";
import RsvpTab        from "./tabs/RsvpTab";
import PublishTab     from "./tabs/PublishTab";
import PreviewTab     from "./tabs/PreviewTab";

/* ─── Re-export types for tabs ───────────────────────────────── */
export type SimpleClient = { id: string; full_name: string; email: string };
export type SimpleJob    = { id: string; title: string };

/* ─── Extended form type with rsvp_message ───────────────────── */
type InvForm = Invitation & { rsvp_message?: string | null };

/* ─── Tab config ─────────────────────────────────────────────── */
type TabId = "general" | "event" | "design" | "media" | "rsvp" | "publish" | "preview";

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "general",  label: "General",       icon: "ri-information-line" },
  { id: "event",    label: "Event Details",  icon: "ri-calendar-event-line" },
  { id: "design",   label: "Design",         icon: "ri-palette-line" },
  { id: "media",    label: "Media",          icon: "ri-image-2-line" },
  { id: "rsvp",     label: "RSVP",           icon: "ri-group-line" },
  { id: "publish",  label: "Publish",        icon: "ri-send-plane-line" },
  { id: "preview",  label: "Preview",        icon: "ri-eye-line" },
];

const EVENT_ICONS: Record<string, string> = {
  Wedding:           "ri-hearts-line",
  "XV Años":         "ri-sparkling-2-line",
  Baptism:           "ri-water-flash-line",
  Graduation:        "ri-graduation-cap-line",
  Birthday:          "ri-cake-2-line",
  "Baby Shower":     "ri-emotion-happy-line",
  Engagement:        "ri-heart-line",
  Anniversary:       "ri-medal-line",
  "Corporate Event": "ri-building-4-line",
  Other:             "ri-star-line",
};

/* ─── Blank form ─────────────────────────────────────────────── */
const BLANK: InvForm = {
  id: "",
  tenant_id: "",
  job_id: null,
  client_id: "",
  contract_id: null,
  portal_token: null,
  design_mode: "template",
  template_id: null,
  custom_template_id: null,
  photo_url: null,
  title: "",
  event_name: null,
  event_title: null,
  host_names: null,
  event_type: "Wedding",
  event_date: null,
  event_time: null,
  event_location: null,
  venue_name: null,
  venue_address: null,
  google_maps_url: null,
  ceremony_name: null,
  ceremony_location: null,
  ceremony_time: null,
  ceremony_address: null,
  ceremony_maps_url: null,
  reception_name: null,
  reception_address: null,
  reception_maps_url: null,
  dress_code: null,
  gift_registry_url: null,
  custom_message: null,
  welcome_message: null,
  thank_you_message: null,
  text_color: "#ffffff",
  text_style: "elegante",
  theme_key: "classic",
  cover_image_url: null,
  music_url: null,
  music_title: null,
  client_music_url: null,
  client_music_name: null,
  gallery_urls: [],
  show_guests: false,
  public_slug: "",
  rsvp_enabled: true,
  is_published: false,
  rsvp_message: null,
  invitation_mode: null,
  custom_template_url: null,
  created_at: "",
  updated_at: "",
};

/* ─── Page ───────────────────────────────────────────────────── */
export default function InvitationDetailPage() {
  const { invitationId } = useParams<{ invitationId: string }>();
  const navigate = useNavigate();
  const isNew = invitationId === "new";

  const [tenantId, setTenantId]   = useState<string | null>(null);
  const [form, setForm]           = useState<InvForm>(BLANK);
  const [clients, setClients]     = useState<SimpleClient[]>([]);
  const [jobs, setJobs]           = useState<SimpleJob[]>([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("general");
  const [dirty, setDirty]         = useState(false);

  const onChange = useCallback(<K extends keyof InvForm>(key: K, val: InvForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    setDirty(true);
  }, []);

  const fetchData = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const [clientRes, jobRes] = await Promise.all([
        supabase.from("clients").select("id, full_name, email").eq("tenant_id", tid).eq("status", "active").order("full_name"),
        supabase.from("jobs").select("id, title").eq("tenant_id", tid).order("title"),
      ]);
      if (clientRes.data) setClients(clientRes.data as SimpleClient[]);
      if (jobRes.data)    setJobs(jobRes.data as SimpleJob[]);

      if (!isNew && invitationId) {
        const { data, error: invErr } = await supabase
          .from("digital_invitations")
          .select("*")
          .eq("id", invitationId)
          .eq("tenant_id", tid)
          .maybeSingle();
        if (invErr) throw new Error(invErr.message);
        if (!data) { setError("Invitation not found."); return; }
        setForm({
          ...BLANK,
          ...(data as InvForm),
          gallery_urls: Array.isArray((data as InvForm).gallery_urls) ? (data as InvForm).gallery_urls : [],
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, [isNew, invitationId]);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchData(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchData]);

  /* ── Save ── */
  const handleSave = async () => {
    if (!tenantId) return;
    if (!form.title.trim() || !form.client_id || !form.event_type) {
      Swal.fire({ title: "Missing fields", text: "Client, event type and internal title are required.", icon: "warning" });
      return;
    }

    setSaving(true);
    const payload = {
      client_id:           form.client_id,
      job_id:              form.job_id || null,
      contract_id:         form.contract_id || null,
      portal_token:        form.portal_token || null,
      design_mode:         form.design_mode || 'template',
      template_id:         form.template_id || null,
      custom_template_id:  form.custom_template_id || null,
      photo_url:           form.photo_url || null,
      title:               form.title,
      event_name:          form.event_name || null,
      event_title:         form.event_title || null,
      host_names:          form.host_names || null,
      event_type:          form.event_type,
      event_date:          form.event_date || null,
      event_time:          form.event_time || null,
      event_location:      form.event_location || null,
      venue_name:          form.venue_name || null,
      venue_address:       form.venue_address || null,
      google_maps_url:     form.google_maps_url || null,
      ceremony_name:       form.ceremony_name || null,
      ceremony_location:   form.ceremony_location || null,
      ceremony_time:       form.ceremony_time || null,
      ceremony_address:    form.ceremony_address || null,
      ceremony_maps_url:   form.ceremony_maps_url || null,
      reception_name:      form.reception_name || null,
      reception_address:   form.reception_address || null,
      reception_maps_url:  form.reception_maps_url || null,
      dress_code:          form.dress_code || null,
      gift_registry_url:   form.gift_registry_url || null,
      custom_message:      form.custom_message || null,
      welcome_message:     form.welcome_message || null,
      thank_you_message:   form.thank_you_message || null,
      text_color:          form.text_color || '#ffffff',
      text_style:          form.text_style || 'elegante',
      cover_image_url:     form.cover_image_url || null,
      music_url:           form.music_url || null,
      music_title:         form.music_title || null,
      client_music_url:    form.client_music_url || null,
      client_music_name:   form.client_music_name || null,
      gallery_urls:        form.gallery_urls ?? [],
      show_guests:         form.show_guests ?? false,
      theme_key:           form.theme_key,
      public_slug:         form.public_slug,
      rsvp_enabled:        form.rsvp_enabled,
      rsvp_message:        form.rsvp_message || null,
      is_published:        form.is_published,
      updated_at:          new Date().toISOString(),
    };

    if (isNew) {
      const { data, error } = await supabase
        .from("digital_invitations")
        .insert({ ...payload, tenant_id: tenantId })
        .select()
        .maybeSingle();

      if (error) {
        Swal.fire({ title: "Error", text: error.message, icon: "error" });
        setSaving(false);
        return;
      }
      if (data) {
        setForm({ ...BLANK, ...(data as InvForm), gallery_urls: Array.isArray((data as InvForm).gallery_urls) ? (data as InvForm).gallery_urls : [] });
        setDirty(false);
        Swal.fire({ title: "Created!", text: "Invitation created successfully.", icon: "success", timer: 1800, showConfirmButton: false });
        navigate(`/admin/invitations/${(data as InvForm).id}`, { replace: true });
      }
    } else {
      const { data, error } = await supabase
        .from("digital_invitations")
        .update(payload)
        .eq("id", form.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();

      if (error) {
        Swal.fire({ title: "Error", text: error.message, icon: "error" });
        setSaving(false);
        return;
      }
      if (data) {
        setForm({ ...BLANK, ...(data as InvForm), gallery_urls: Array.isArray((data as InvForm).gallery_urls) ? (data as InvForm).gallery_urls : [] });
        setDirty(false);
        Swal.fire({ title: "Saved!", text: "Changes saved successfully.", icon: "success", timer: 1500, showConfirmButton: false });
      }
    }
    setSaving(false);
  };

  const handleCopyLink = () => {
    if (!form.public_slug) return;
    const url = `${window.location.origin}/invite/${form.public_slug}`;
    navigator.clipboard.writeText(url);
    Swal.fire({ title: "Copied!", text: url, icon: "success", timer: 1500, showConfirmButton: false });
  };

  const eventIcon = EVENT_ICONS[form.event_type] ?? "ri-star-line";

  return (
    <AdminLayout>
      <ModuleGate moduleId="digital_invitations">
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={() => tenantId && fetchData(tenantId)} showLogin />
        ) : (
          <div className="min-h-screen bg-slate-50">
            {/* ── Top bar ── */}
            <div className="bg-white border-b border-slate-200 px-4 md:px-8 py-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                {/* Breadcrumb + title */}
                <div className="flex items-center gap-3 min-w-0">
                  <Link
                    to="/admin/invitations"
                    className="flex items-center gap-1.5 text-slate-400 hover:text-slate-600 text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line"></i>
                    </span>
                    Invitations
                  </Link>
                  <span className="text-slate-200">/</span>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 shrink-0">
                      <i className={`${eventIcon} text-sm`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-slate-800 font-semibold text-sm truncate">
                        {isNew ? "New Invitation" : (form.event_title || form.title || "Untitled")}
                      </p>
                      {!isNew && (
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                            form.is_published
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-500"
                          }`}>
                            {form.is_published ? "Live" : "Draft"}
                          </span>
                          <span className="text-slate-400 text-xs">{form.event_type}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  {dirty && (
                    <span className="text-amber-500 text-xs flex items-center gap-1">
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-circle-fill text-[6px]"></i>
                      </span>
                      Unsaved changes
                    </span>
                  )}
                  {!isNew && form.is_published && form.public_slug && (
                    <a
                      href={`/invite/${form.public_slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 border border-slate-200 text-slate-500 hover:text-slate-700 text-sm px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-external-link-line"></i>
                      </span>
                      View Live
                    </a>
                  )}
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {saving ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-save-line"></i>
                      </span>
                    )}
                    {saving ? "Saving…" : isNew ? "Create Invitation" : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>

            {/* ── Tabs ── */}
            <div className="bg-white border-b border-slate-200 px-4 md:px-8">
              <div className="flex items-center gap-0 overflow-x-auto">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap -mb-px ${
                      activeTab === tab.id
                        ? "border-indigo-500 text-indigo-600"
                        : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-200"
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${tab.icon} text-sm`}></i>
                    </span>
                    {tab.label}
                    {tab.id === "rsvp" && !isNew && (
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-600 font-semibold">
                        RSVP
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Tab content ── */}
            <div className="px-4 md:px-8 py-6 max-w-3xl">
              {activeTab === "general" && (
                <GeneralTab form={form} clients={clients} jobs={jobs} onChange={onChange} />
              )}
              {activeTab === "event" && (
                <EventDetailsTab form={form} onChange={onChange} />
              )}
              {activeTab === "design" && (
                <DesignTab form={form} onChange={onChange} />
              )}
              {activeTab === "media" && (
                <MediaTab form={form} onChange={onChange} tenantId={tenantId ?? undefined} />
              )}
              {activeTab === "rsvp" && tenantId && (
                <RsvpTab form={form} tenantId={tenantId} onChange={onChange as Parameters<typeof RsvpTab>[0]["onChange"]} />
              )}
              {activeTab === "publish" && (
                <PublishTab form={form} onChange={onChange} onCopyLink={handleCopyLink} />
              )}
              {activeTab === "preview" && (
                <PreviewTab form={form} />
              )}
            </div>

            {/* ── Floating save bar (when dirty) ── */}
            {dirty && (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
                <div className="flex items-center gap-3 bg-[#0f172a] text-white px-5 py-3 rounded-2xl shadow-lg">
                  <span className="w-4 h-4 flex items-center justify-center text-amber-400">
                    <i className="ri-circle-fill text-[6px]"></i>
                  </span>
                  <p className="text-sm">You have unsaved changes</p>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1.5 bg-[#2563eb] hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold px-4 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {saving ? "Saving…" : "Save now"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </ModuleGate>
    </AdminLayout>
  );
}
